import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Borrower } from '@/types';
import type { AbnLookupResult, AbnSearchResult, BorrowerEntityType, Director } from '@/types';

interface BorrowerListResponse {
  data: Borrower[];
  total: number;
  limit: number;
  offset: number;
}

export function useBorrowers(params: { search?: string; limit?: number; page?: number } = {}) {
  return useQuery<BorrowerListResponse>({
    queryKey: ['borrowers', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.search) searchParams.set('search', params.search);
      if (params.limit) searchParams.set('limit', String(Math.min(params.limit ?? 25, 100)));
      if (params.page) searchParams.set('page', String(params.page));
      const qs = searchParams.toString();
      const { data } = await api.get<BorrowerListResponse>(`/borrowers${qs ? `?${qs}` : ''}`);
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

async function fetchBorrower(id: string): Promise<Borrower & { loans?: any[] }> {
  const { data } = await api.get(`/borrowers/${id}`);
  return data;
}

export function useBorrower(id: string) {
  return useQuery({
    queryKey: ['borrowers', id],
    queryFn: () => fetchBorrower(id),
    enabled: !!id,
  });
}

interface CreateBorrowerPayload {
  name: string;
  email?: string;
  phone?: string;
  taxId?: string;
  address?: Record<string, string>;
  entityType?: BorrowerEntityType;
  abn?: string;
  acn?: string;
  abnStatus?: string;
  entityName?: string;
  gstRegistered?: boolean;
  trustType?: string;
  trusteeType?: string;
  trusteeName?: string;
  directors?: Director[];
}

async function createBorrower(payload: CreateBorrowerPayload): Promise<Borrower> {
  const { data } = await api.post<Borrower>('/borrowers', payload);
  return data;
}

export function useCreateBorrower() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createBorrower,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['borrowers'] });
    },
  });
}

interface UpdateBorrowerPayload {
  name?: string;
  email?: string;
  phone?: string;
  taxId?: string;
  address?: Record<string, string>;
  entityType?: BorrowerEntityType;
  abn?: string;
  acn?: string;
  abnStatus?: string;
  entityName?: string;
  gstRegistered?: boolean;
  trustType?: string;
  trusteeType?: string;
  trusteeName?: string;
  directors?: Director[];
}

export function useUpdateBorrower() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateBorrowerPayload & { id: string }) => {
      const { data: updated } = await api.patch(`/borrowers/${id}`, data);
      return updated;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['borrowers'] });
      qc.invalidateQueries({ queryKey: ['borrowers', vars.id] });
    },
  });
}

// ABN Lookup Hooks

export function useAbnValidate(abn: string) {
  return useQuery({
    queryKey: ['abn-validate', abn],
    queryFn: async () => {
      const { data } = await api.get<{ valid: boolean }>(`/borrowers/abn-validate/${abn}`);
      return data;
    },
    enabled: !!abn && abn.replace(/\s+/g, '').length === 11,
  });
}

export function useAbnLookup(abn: string, enabled = true) {
  return useQuery({
    queryKey: ['abn-lookup', abn],
    queryFn: async () => {
      const { data } = await api.get<AbnLookupResult>(
        `/borrowers/abn-lookup/${abn.replace(/\s+/g, '')}`,
      );
      return data;
    },
    enabled: enabled && !!abn && abn.replace(/\s+/g, '').length === 11,
  });
}

export function useAbnSearch(name: string) {
  return useQuery({
    queryKey: ['abn-search', name],
    queryFn: async () => {
      const { data } = await api.get<{ results: AbnSearchResult[] }>(
        `/borrowers/abn-search?name=${encodeURIComponent(name)}`,
      );
      return data.results;
    },
    enabled: !!name && name.trim().length >= 3,
  });
}

export function useVerifyBorrowerAbn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ borrowerId, abn }: { borrowerId: string; abn: string }) => {
      const { data } = await api.post(`/borrowers/${borrowerId}/verify-abn`, { abn });
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['borrowers', vars.borrowerId] });
      qc.invalidateQueries({ queryKey: ['borrowers'] });
    },
  });
}

export function useDeleteBorrower() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/borrowers/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['borrowers'] });
    },
  });
}
